import React, { useState, useEffect } from 'react';
import { 
  Users, AlertTriangle, Crown, UserPlus, TrendingUp,
  ChevronRight, Phone, Mail, MapPin, Calendar, DollarSign,
  Clock, RefreshCw, AlertCircle, Star, ArrowRight, ExternalLink
} from 'lucide-react';
import { formatCurrency } from '../../utils/helpers';
import { reportsApi } from '../../services/api';
import Loading from '../Loading';

const CustomerIntelligence = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('at-risk');
  const [expandedCustomer, setExpandedCustomer] = useState(null);

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await reportsApi.getCustomerIntelligence();
      setData(response.data);
    } catch (err) {
      console.error('Error loading customer intelligence:', err);
      setError('Unable to load customer intelligence');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading />;

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
        <p className="text-gray-600">{error}</p>
        <button onClick={loadReport} className="btn btn-primary mt-4">
          Try Again
        </button>
      </div>
    );
  }

  if (!data) return null;

  const sections = [
    { 
      id: 'at-risk', 
      label: 'At-Risk', 
      icon: AlertTriangle, 
      count: data.atRiskCount,
      color: 'text-red-600 bg-red-100',
      description: 'Repeat customers overdue for service'
    },
    { 
      id: 'vip', 
      label: 'VIP', 
      icon: Crown, 
      count: data.vipCount,
      color: 'text-amber-600 bg-amber-100',
      description: 'Top 10% by lifetime value'
    },
    { 
      id: 'win-back', 
      label: 'Win-Back', 
      icon: UserPlus, 
      count: data.winBackCount,
      color: 'text-blue-600 bg-blue-100',
      description: 'Inactive customers to re-engage'
    },
    { 
      id: 'upsell', 
      label: 'Upsell', 
      icon: TrendingUp, 
      count: data.upsellCount,
      color: 'text-green-600 bg-green-100',
      description: 'Opportunities to expand services'
    },
  ];

  const getRiskBadge = (riskLevel) => {
    switch (riskLevel) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-200';
      default: return 'bg-amber-100 text-amber-700 border-amber-200';
    }
  };

  const getWinBackBadge = (reason) => {
    switch (reason) {
      case 'seasonal': return 'bg-blue-100 text-blue-700';
      case 'high-value': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatPhone = (phone) => {
    if (!phone) return null;
    // Basic formatting
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="card p-4 bg-gradient-to-br from-primary-50 to-indigo-50 border-primary-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-primary-600 font-medium">Total Customers</p>
              <p className="text-2xl font-bold text-primary-900">{data.totalActiveCustomers}</p>
            </div>
            <Users className="text-primary-500" size={28} />
          </div>
        </div>

        <div className="card p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-green-600 font-medium">Potential Revenue</p>
              <p className="text-2xl font-bold text-green-900">{formatCurrency(data.totalPotentialRevenue)}</p>
            </div>
            <DollarSign className="text-green-500" size={28} />
          </div>
        </div>

        <div className="card p-4">
          <p className="text-xs text-gray-500">Retention Rate</p>
          <p className="text-xl font-bold text-gray-900">{data.healthSummary?.retentionRate?.toFixed(0) || 0}%</p>
          <p className="text-xs text-gray-500">customers return</p>
        </div>

        <div className="card p-4">
          <p className="text-xs text-gray-500">Avg Lifetime Value</p>
          <p className="text-xl font-bold text-gray-900">{formatCurrency(data.healthSummary?.averageCustomerLifetimeValue || 0)}</p>
          <p className="text-xs text-gray-500">per customer</p>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {sections.map((section) => {
          const Icon = section.icon;
          const isActive = activeSection === section.id;
          return (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap transition-all ${
                isActive 
                  ? 'bg-gray-900 text-white shadow-lg' 
                  : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon size={16} />
              <span className="font-medium">{section.label}</span>
              {section.count > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                  isActive ? 'bg-white/20 text-white' : section.color
                }`}>
                  {section.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Section Description */}
      <p className="text-sm text-gray-600">
        {sections.find(s => s.id === activeSection)?.description}
      </p>

      {/* AT-RISK CUSTOMERS */}
      {activeSection === 'at-risk' && (
        <div className="space-y-3">
          {data.atRiskCustomers.length === 0 ? (
            <div className="card p-8 text-center">
              <AlertTriangle className="mx-auto h-12 w-12 text-green-400 mb-4" />
              <p className="text-gray-600 font-medium">No at-risk customers!</p>
              <p className="text-sm text-gray-500 mt-1">All your repeat customers are booking on schedule.</p>
            </div>
          ) : (
            data.atRiskCustomers.map((customer) => (
              <div 
                key={customer.customerId} 
                className="card p-0 overflow-hidden hover:shadow-md transition-shadow"
              >
                <div 
                  className="p-4 cursor-pointer"
                  onClick={() => setExpandedCustomer(
                    expandedCustomer === `risk-${customer.customerId}` ? null : `risk-${customer.customerId}`
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{customer.customerName}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getRiskBadge(customer.riskLevel)}`}>
                          {customer.riskLevel}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Last service: {formatDate(customer.lastJobDate)} ({customer.daysSinceLastJob} days ago)
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        <Clock size={12} className="inline mr-1" />
                        {customer.daysOverdue} days overdue (typically every {customer.typicalDaysBetweenJobs} days)
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-green-600">{formatCurrency(customer.estimatedLostRevenue)}</p>
                      <p className="text-xs text-gray-500">potential</p>
                      <ChevronRight 
                        size={18} 
                        className={`text-gray-400 mt-2 transition-transform ${
                          expandedCustomer === `risk-${customer.customerId}` ? 'rotate-90' : ''
                        }`}
                      />
                    </div>
                  </div>
                </div>

                {expandedCustomer === `risk-${customer.customerId}` && (
                  <div className="px-4 pb-4 pt-0 border-t border-gray-100 bg-gray-50">
                    <div className="grid grid-cols-2 gap-4 py-3 text-sm">
                      <div>
                        <span className="text-gray-500">Lifetime Value:</span>
                        <span className="font-medium ml-2">{formatCurrency(customer.lifetimeValue)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Total Jobs:</span>
                        <span className="font-medium ml-2">{customer.totalJobs}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Avg Job:</span>
                        <span className="font-medium ml-2">{formatCurrency(customer.averageJobValue)}</span>
                      </div>
                      {customer.lastServiceType && (
                        <div>
                          <span className="text-gray-500">Last Service:</span>
                          <span className="font-medium ml-2">{customer.lastServiceType}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 pt-2">
                      {customer.phone && (
                        <a 
                          href={`tel:${customer.phone}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                        >
                          <Phone size={14} />
                          Call
                        </a>
                      )}
                      {customer.email && (
                        <a 
                          href={`mailto:${customer.email}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                        >
                          <Mail size={14} />
                          Email
                        </a>
                      )}
                      <a 
                        href={`/customers?id=${customer.customerId}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300"
                      >
                        View <ExternalLink size={14} />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* VIP CUSTOMERS */}
      {activeSection === 'vip' && (
        <div className="space-y-3">
          {data.vipCustomers.length === 0 ? (
            <div className="card p-8 text-center">
              <Crown className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <p className="text-gray-600 font-medium">No VIP customers yet</p>
              <p className="text-sm text-gray-500 mt-1">Build up repeat customers to identify your VIPs.</p>
            </div>
          ) : (
            data.vipCustomers.map((customer, index) => (
              <div 
                key={customer.customerId} 
                className="card p-4 hover:shadow-md transition-shadow border-l-4 border-l-amber-400"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </span>
                      <h3 className="font-semibold text-gray-900">{customer.customerName}</h3>
                      {customer.isOverdueForService && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs">
                          Overdue
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 mt-2">
                      <span className="flex items-center gap-1">
                        {formatCurrency(customer.lifetimeValue)} lifetime
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {customer.totalJobs} jobs
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {customer.monthsAsCustomer} months
                      </span>
                      {customer.city && (
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {customer.city}
                        </span>
                      )}
                    </div>
                    {customer.servicesUsed.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {customer.servicesUsed.map((service, i) => (
                          <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                            {service}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-lg font-bold text-amber-600">{formatCurrency(customer.averageJobValue)}</p>
                    <p className="text-xs text-gray-500">avg job</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* WIN-BACK OPPORTUNITIES */}
      {activeSection === 'win-back' && (
        <div className="space-y-3">
          {data.winBackOpportunities.length === 0 ? (
            <div className="card p-8 text-center">
              <UserPlus className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <p className="text-gray-600 font-medium">No win-back opportunities</p>
              <p className="text-sm text-gray-500 mt-1">All past customers are either active or have been gone too long.</p>
            </div>
          ) : (
            data.winBackOpportunities.map((customer) => (
              <div 
                key={customer.customerId} 
                className="card p-0 overflow-hidden hover:shadow-md transition-shadow"
              >
                <div 
                  className="p-4 cursor-pointer"
                  onClick={() => setExpandedCustomer(
                    expandedCustomer === `winback-${customer.customerId}` ? null : `winback-${customer.customerId}`
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{customer.customerName}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getWinBackBadge(customer.winBackReason)}`}>
                          {customer.winBackReason === 'seasonal' ? '🌱 Seasonal' : 
                           customer.winBackReason === 'high-value' ? '⭐ High Value' : '📅 Anniversary'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Last service: {formatDate(customer.lastJobDate)} ({customer.monthsSinceLastJob} months ago)
                      </p>
                      {customer.city && (
                        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                          <MapPin size={12} />
                          {customer.address && `${customer.address}, `}{customer.city}
                        </p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-blue-600">{formatCurrency(customer.estimatedWinBackValue)}</p>
                      <p className="text-xs text-gray-500">potential</p>
                    </div>
                  </div>
                </div>

                {expandedCustomer === `winback-${customer.customerId}` && (
                  <div className="px-4 pb-4 pt-0 border-t border-gray-100 bg-gray-50">
                    <div className="grid grid-cols-2 gap-4 py-3 text-sm">
                      <div>
                        <span className="text-gray-500">Lifetime Value:</span>
                        <span className="font-medium ml-2">{formatCurrency(customer.lifetimeValue)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Past Jobs:</span>
                        <span className="font-medium ml-2">{customer.totalJobs}</span>
                      </div>
                      {customer.lastServiceType && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Last Service:</span>
                          <span className="font-medium ml-2">{customer.lastServiceType}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 pt-2">
                      {customer.phone && (
                        <a 
                          href={`tel:${customer.phone}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                        >
                          <Phone size={14} />
                          Call
                        </a>
                      )}
                      {customer.email && (
                        <a 
                          href={`mailto:${customer.email}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                        >
                          <Mail size={14} />
                          Email
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* UPSELL CANDIDATES */}
      {activeSection === 'upsell' && (
        <div className="space-y-3">
          {data.upsellCandidates.length === 0 ? (
            <div className="card p-8 text-center">
              <TrendingUp className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <p className="text-gray-600 font-medium">No upsell opportunities</p>
              <p className="text-sm text-gray-500 mt-1">Add more service types to identify upsell potential.</p>
            </div>
          ) : (
            data.upsellCandidates.map((customer) => (
              <div 
                key={customer.customerId} 
                className="card p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{customer.customerName}</h3>
                    <p className="text-sm text-gray-600">
                      {customer.totalJobs} jobs • {formatCurrency(customer.lifetimeValue)} lifetime
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-600">{formatCurrency(customer.estimatedUpsellValue)}</p>
                    <p className="text-xs text-gray-500">potential</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm mb-2">
                  <span className="text-gray-500">Current:</span>
                  <div className="flex flex-wrap gap-1">
                    {customer.currentServices.map((service, i) => (
                      <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                        {service}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex items-start gap-2 text-sm">
                  <ArrowRight size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-gray-500">Suggest:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {customer.suggestedServices.map((service, i) => (
                        <span 
                          key={i} 
                          className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs"
                          title={service.reason}
                        >
                          {service.serviceName}
                          {service.defaultPrice && ` (${formatCurrency(service.defaultPrice)})`}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                  {customer.phone && (
                    <a 
                      href={`tel:${customer.phone}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                    >
                      <Phone size={14} />
                      Call
                    </a>
                  )}
                  {customer.email && (
                    <a 
                      href={`mailto:${customer.email}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                    >
                      <Mail size={14} />
                      Email
                    </a>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Refresh Button */}
      <div className="text-center pt-4">
        <button
          onClick={loadReport}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
        >
          <RefreshCw size={14} />
          Refresh Report
        </button>
      </div>
    </div>
  );
};

export default CustomerIntelligence;